import { BaseApiClient } from "../api/baseApiClient.service";
import type { JWTPayload } from "../auth/auth.types";

export type Visibility = "PUBLIC" | "PRIVATE";

export type PodcastCreationJobStatus =
	| "PENDING"
	| "PROCESSING"
	| "COMPLETED"
	| "FAILED";
export type PodcastChapterCreationJobStatus =
	| "CREATED"
	| "SENT"
	| "PENDING"
	| "SUCCESS"
	| "FAILED";

export interface PodcastChapterCreationJob {
	id: string;
	createdAt: string;
	updatedAt: string;
	podcastChapterId: string;
	status: PodcastChapterCreationJobStatus;
}

export interface PodcastChapter {
	id: string;
	title: string;
	s3Path: string | null;
	script: string;
	extractedText: string;
	language: string;
	index: number;
	length: number; // length of the chapter audio in milliseconds
	podcastId: string;
	included: boolean;
	creationJob?: PodcastChapterCreationJob;
}

export interface StorageFile {
	id: string;
	blobLink: string;
	createdAt: string;
	updatedAt: string;
}

export interface PodWorkspace {
	id: string;
	title: string;
	visibility: Visibility;
	createdAt: string;
	createdById: string;
}

export interface PodcastProgress {
	currentTime: number; // in seconds
	duration: number; // in seconds
	completed: boolean;
	lastListenedAt: string; // ISO timestamp
	progressPercentage: number; // 0-100
}

export interface EmbeddingStatus {
	status: "PENDING" | "PROCESSING" | "COMPLETED" | "FAILED" | "PARTIAL";
	totalChapters: number;
	completedChapters: number;
	failedChapters: number;
	processingChapters: number;
	lastEmbeddedAt: string | null;
}

export interface Podcast {
	id: string;
	title: string;
	createdById: string;
	createdBy?: JWTPayload;
	joinedPodcastFileId: string;
	joinedPodcastFile?: StorageFile;
	visibility: Visibility;
	transcription: string;
	language: string;
	chapters: PodcastChapter[];
	length: number;
	podWorkspaceId: string;
	podWorkspace?: PodWorkspace;
	status: PodcastCreationJobStatus;
	/** When false and status is PENDING, generation has not been charged / started yet. */
	costApproved?: boolean;
	costApprovedAt?: string | null;
	progress?: PodcastProgress;
	embeddingStatus?: EmbeddingStatus;
	retryCount?: number;
}

export interface Chapter {
	startTime: number;
	endTime: number;
	text: string;
}

// ============================================================
// Transcript types (from /processing/podcasts/:id/transcript)
// ============================================================

export interface TranscriptSegment {
	id: string;
	chunkIndex: number;
	text: string;
	/**
	 * Multi-language translation map: ISO 639-1 code → translated speaker text.
	 * e.g. { "en": "Speaker 1: Hello...", "fr": "Speaker 1: Bonjour..." }
	 */
	translations: Record<string, string>;
	startTimeMs: number;
	durationMs: number;
	/** Pre-computed global start time in milliseconds across the full podcast */
	globalStartMs: number;
}

export interface TranscriptChapter {
	id: string;
	index: number;
	title: string;
	lengthMs: number;
	globalStartMs: number;
	hasTranscript: boolean;
	segments: TranscriptSegment[];
}

export interface PodcastTranscript {
	podcastId: string;
	totalLengthMs: number;
	chapters: TranscriptChapter[];
}

/**
 * Service for interacting with the podcast API.
 */
export class PodcastService {
	static path = "podcast";

	/**
	 * Retry podcast generation
	 */
	static async retryPodcast(podcastId: string): Promise<{ message: string }> {
		return BaseApiClient.getInstance().post<
			{ message: string },
			Record<string, never>
		>(`${PodcastService.path}/${podcastId}/retry`, {});
	}

	/**
	 * Get all podcasts for the current user
	 */
	static async getAllPodcasts(): Promise<Podcast[]> {
		return BaseApiClient.getInstance().get<Podcast[]>(
			`${PodcastService.path}/all`,
		);
	}

	/**
	 * Habit Loop v1 - summary of the user's completed podcasts.
	 * Used by the personalized trial banner.
	 */
	static async getSummaryStats(): Promise<{
		podcastCount: number;
		totalDurationMinutes: number;
	}> {
		return BaseApiClient.getInstance().get<{
			podcastCount: number;
			totalDurationMinutes: number;
		}>(`${PodcastService.path}/summary-stats`);
	}

	/**
	 * Get podcasts by workspace ID
	 * This is the function that will be used to get the podcasts by workspace ID when a podcast is being played
	 */
	static async getPodcastsByWorkspace(
		podWorkspaceId: string,
	): Promise<Podcast[]> {
		return BaseApiClient.getInstance().get<Podcast[]>(
			`${PodcastService.path}/workspace/${podWorkspaceId}`,
		);
	}

	/**
	 * Get a single podcast by ID (includes chapters)
	 */
	static async getPodcastById(podcastId: string): Promise<Podcast> {
		return BaseApiClient.getInstance().get<Podcast>(
			`${PodcastService.path}/${podcastId}`,
		);
	}

	/**
	 * Transform backend PodcastChapter[] to player Chapter[] format
	 * Uses the actual chapter lengths from the database
	 * Filters out chapters that are not included or have failed
	 */
	static transformChaptersForPlayer(
		chapters: PodcastChapter[],
		totalDuration: number,
	): Chapter[] {
		if (!chapters || chapters.length === 0) {
			return [];
		}

		// Filter out chapters that shouldn't be played:
		// - Not included (included = false)
		// - Failed to generate (creationJob.status = FAILED)
		// - No audio file (s3Path = null)
		const playableChapters = chapters.filter((chapter) => {
			// Must be included
			if (!chapter.included) return false;

			// Must have audio file
			if (!chapter.s3Path) return false;

			// If has creation job, it must not be failed
			if (chapter.creationJob && chapter.creationJob.status === "FAILED")
				return false;

			return true;
		});

		// Sort chapters by index to ensure correct order
		const sortedChapters = [...playableChapters].sort(
			(a, b) => a.index - b.index,
		);

		if (sortedChapters.length === 0) {
			return [];
		}

		// Check if chapters have length information
		const hasLengthInfo = sortedChapters.some((ch) => ch.length > 0);

		if (hasLengthInfo) {
			// Use actual chapter lengths from database (convert from milliseconds to seconds)
			let currentTime = 0;
			return sortedChapters.map((chapter) => {
				const startTime = currentTime;
				const duration = chapter.length / 1000; // Convert ms to seconds
				const endTime = startTime + duration;
				currentTime = endTime;
				return {
					startTime,
					endTime,
					text: chapter.title,
				};
			});
		}

		// Fallback: distribute evenly if no length info available
		const timePerChapter = totalDuration / sortedChapters.length;
		return sortedChapters.map((chapter, idx) => ({
			startTime: idx * timePerChapter,
			endTime: (idx + 1) * timePerChapter,
			text: chapter.title,
		}));
	}

	/**
	 * Get chapters for a podcast in player format
	 */
	static async getChaptersForPlayer(
		podcastId: string,
		audioDuration: number,
	): Promise<Chapter[]> {
		const podcast = await PodcastService.getPodcastById(podcastId);
		return PodcastService.transformChaptersForPlayer(
			podcast.chapters,
			audioDuration,
		);
	}

	/**
	 * Get presigned URL for podcast audio file
	 */
	static async getPodcastAudioUrl(
		podcastId: string,
	): Promise<{ url: string; expiresIn: number }> {
		return BaseApiClient.getInstance().get<{ url: string; expiresIn: number }>(
			`${PodcastService.path}/${podcastId}/audio-url`,
		);
	}

	/**
	 * Trigger audio generation for a podcast
	 * This returns immediately after starting the background process
	 */
	static async triggerAudioGeneration(podcastId: string): Promise<{
		message: string;
		podcastId: string;
		chaptersCount: number;
		status: string;
	}> {
		return BaseApiClient.getInstance().post<
			{
				message: string;
				podcastId: string;
				chaptersCount: number;
				status: string;
			},
			Record<string, never>
		>(`processing/audio-generation/${podcastId}`, {});
	}

	/**
	 * Get the full transcript for a podcast with global timestamps.
	 * Returns chapters and their segments, each with a globalStartMs offset.
	 */
	static async getTranscript(podcastId: string): Promise<PodcastTranscript> {
		return BaseApiClient.getInstance().get<PodcastTranscript>(
			`processing/podcasts/${podcastId}/transcript`,
		);
	}

	/**
	 * Trigger translation of all transcript segments for one chapter.
	 * Returns immediately; result arrives via backend callback and persisted to DB.
	 */
	static async triggerChapterTranslation(
		podcastId: string,
		chapterId: string,
		targetLanguage: string,
		sourceLanguage?: string,
	): Promise<{ message: string; totalSegments: number }> {
		return BaseApiClient.getInstance().post<
			{ message: string; totalSegments: number },
			{ targetLanguage: string; sourceLanguage?: string }
		>(`processing/podcasts/${podcastId}/chapters/${chapterId}/translate`, {
			targetLanguage,
			...(sourceLanguage ? { sourceLanguage } : {}),
		});
	}

	/**
	 * Trigger translation for all chapters of a podcast that have transcript segments.
	 * Fires one request per chapter in parallel and returns when all are submitted.
	 */
	static async triggerPodcastTranslation(
		podcastId: string,
		chapterIds: string[],
		targetLanguage: string,
		sourceLanguage?: string,
	): Promise<void> {
		await Promise.allSettled(
			chapterIds.map((id) =>
				PodcastService.triggerChapterTranslation(
					podcastId,
					id,
					targetLanguage,
					sourceLanguage,
				),
			),
		);
	}

	/**
	 * Approve cost for podcast generation
	 * This starts the script and audio generation process
	 */
	static async approveCost(podcastId: string): Promise<{
		success: boolean;
		message: string;
		podcast: Podcast;
	}> {
		return BaseApiClient.getInstance().post<
			{
				success: boolean;
				message: string;
				podcast: Podcast;
			},
			Record<string, never>
		>(`${PodcastService.path}/${podcastId}/approve-cost`, {});
	}
}
